import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DocumentMobileTab } from '../../lib/documentHelpers';
import { ClipboardList, FileText, type LucideIcon } from 'lucide-react';

interface DocumentMobileTabsProps {
  activeTab: DocumentMobileTab;
  onTabChange: (tab: DocumentMobileTab) => void;
}

const DocumentMobileTabs: React.FC<DocumentMobileTabsProps> = ({ activeTab, onTabChange }) => {
  const { t } = useLanguage();

  const tabs: { id: DocumentMobileTab; label: string; icon: LucideIcon }[] = [
    { id: 'details', label: t('documentTabDetails'), icon: ClipboardList },
    { id: 'document', label: t('documentTabDocument'), icon: FileText },
  ];

  return (
    <div
      className="flex shrink-0 gap-1 rounded-lg border bg-muted/50 p-1"
      role="tablist"
      aria-label={t('documentTabListLabel')}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => {
              if (activeTab !== tab.id) {
                captureEvent('document_mobile_tab_selected', { tab: tab.id });
              }
              onTabChange(tab.id);
            }}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default DocumentMobileTabs;
